<script setup>
import { use } from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';
import {onMounted, ref, watch} from 'vue';
import * as echarts from 'echarts/core';
import {detectMinMax} from '../../helper';
import {getTenantData} from '@/services/TenantService';
import {formatDate, formatAmount, formatNumber, formatPercents, roundMinMaxValues} from '../../helper';

use([ TooltipComponent, GridComponent, BarChart, LegendComponent, SVGRenderer]);

const theme = ref({});

const maxVisibleCategories = 5;
const maxVisibleYAxisLines = 3;


const formatChartTooltipDateAndNumberOrAmount = (params) => {

  let string = '';
  let firstParam = params[0];
  string += formatDate(firstParam.axisValue, 'd.m.Y');

  params.forEach((param, index) => {
    string += '<br/>';
    string += param.marker + ' ' + param.seriesName + ': ';
    if(props.valuesType === 'amount') {
      string += formatAmount(param.value, props.currency);
    } else {
      string += formatNumber(param.value);
    }
  });
  return string;

}

const defaultSerie = {
  name: '',
  smooth: true,
  symbol: 'none',
  type: 'bar',
  stack: 'a',
  data: []
}

let prepared = ref(false);

const props = defineProps({
  series: Array,
  categories: Array,
  valuesType: 'number' | 'amount',
  currency: 'CZK' | 'EUR' | null,
  view: 'minimal' | null
})

watch(() => props.series, (first, second) => {
    prepareChartData();
});

const interval = Math.ceil(props.categories.length / maxVisibleCategories);

const defaultOptions = ref({
  legend: {
    orient: 'horizontal',
    bottom: 0,
    left: 'center'   
  },
  tooltip: {
    trigger: 'axis',
  },

  xAxis: {
    type: 'category',
    axisTick: {
      show: false,
    },
    axisLine: {
      show: false
    },
    axisLabel: {
      align: 'left',
      interval: interval,
      color: 'rgba(0,65,72,.5)',
      formatter: function (value, index) {
        return formatDate(value, 'd.m.Y');
      }
    }
  },
  yAxis: {
    type: 'value',
    position: 'left',
    scale: true,
    splitLine: {
      show: true,
    },
    axisLine: {
      onZero: false
    },
    axisLabel: {
      inside: true,
      showMinLabel: false,
      showMaxLabel: true,
      verticalAlign: 'top',
      interval: 20,
      color: 'rgba(0,65,72,.5)',
      formatter: function(value, index) {
        if(props.valuesType === 'amount') {
          return formatAmount(value, props.currency);
        } else {
          return formatNumber(value);
        }
      }
    },
    axisTick: {
      show: false
    },
  },
  grid: {
    left: '0',
    right: 0,
    top: 0,
    bottom: '50px',
    width: 'auto',
    height: 'auto',
    show: false
  },
  series: []
});

defaultOptions.value['tooltip']['formatter'] = function(params) {
  return formatChartTooltipDateAndNumberOrAmount(params);
};

const defaultOptionsMinimal = ref({
  tooltip: {
    trigger: 'axis',
  },
  xAxis: {
    type: 'time',
    boundaryGap: false
  },
  yAxis: {
    type: 'value',
    boundaryGap: [0, '100%'],
    splitLine: {
      show: false
    },
  },
  grid: {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    width: 'auto',
    height: 'auto',
    show: false
  },
  series: []
});

defaultOptionsMinimal.value['tooltip']['formatter'] = function(params) {
  return formatChartTooltipDateAndNumberOrAmount(params);
};

const options = ref({});


onMounted(() => {
  getTenantData()
      .then((response) => {
        theme.value = response.theme;
        prepareChartData();
      });
});

function prepareChartData() {
  let preparedSeries = [];
  let min = 9999999999;
  let max = -999999999;
  let tmpMinMax = null;

  // loop through series and prepare them
  props.series.forEach(serie => {

    // prepare serie to wanted format
    preparedSeries.push(prepareSerie(serie));

    // detect min value
    tmpMinMax = detectMinMax(serie.data);
    if(tmpMinMax[0] < min) {
      min = tmpMinMax[0];
    }
    if(tmpMinMax[1] > max) {
      max = tmpMinMax[1];
    }
  });

  options.value = props.view === 'minimal' ? defaultOptionsMinimal.value : defaultOptions.value;
  options.value.interval = undefined;

  // refactor min a max - round to rounded values (to 100 or 1000)
  let rounded = roundMinMaxValues(min, max)
  min = rounded.min;
  max = rounded.max;

  
  // update options with series, min value and max value
  options.value.series = preparedSeries;
  options.value.yAxis.min = min;
  options.value.yAxis.max = max;
  const interval = rounded.step ?? (max - min) / maxVisibleYAxisLines;
  if (interval > 0) {
    options.value.yAxis.interval = interval;
  }

  if(props.categories && props.view !== 'minimal')
    options.value.xAxis.data = props.categories;



  // set switch that data are prepared
  prepared.value = true;

};


/**
 * prepare serie, merge with default etc...
 */
function prepareSerie(serie) {
  let preparedSerie = {...defaultSerie, ...serie};

  // prepare item style
  if(!preparedSerie.itemStyle) {
    preparedSerie.itemStyle = {
      color: theme.value.primaryColor ? theme.value.primaryColor : 'rgb(20, 55, 180)'
    };
  }

  let usedColor = preparedSerie.itemStyle.color;

  // prepare area style
  if(!preparedSerie.areaStyle) {
    preparedSerie.areaStyle = {
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        {
          offset: 0,
          color: usedColor
        },
        {
          offset: .75,
          color: theme.value.primaryInterfaceColor ? theme.value.primaryInterfaceColor : 'rgb(255, 255, 255)'
        },
        {
          offset: 1,
          color: 'rgba(255, 255, 255, 0)'
        }
      ])
    };
  }

  return preparedSerie;
}

</script>
<template>
  <v-chart v-if="prepared" class="chart" :option="options" autoresize />
</template>
<style scoped>
/* .chart {
  height: 200px;
} */
</style>
